import React, { useState, useEffect } from 'react';
import { Video, Check, Loader2, Sparkles } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { getReferralCode } from '../utils/referral';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SocialVideoAddOnPage() {
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/subscriptions/social-video/info`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null));
  }, []);

  const onSubscribe = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/subscriptions/social-video/checkout`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        origin_url: window.location.origin,
        ref: getReferralCode(),
      }, { withCredentials: true });
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
      } else {
        setError('Could not start checkout. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Stripe checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="section-padding bg-[#FAFAFA]" data-testid="social-video-addon-page">
        <div className="container-custom max-w-4xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#EFF6FF] text-[#1D4ED8] px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <Sparkles size={14} /> Optional Add-On
            </div>
            <h1 className="heading-1 text-3xl lg:text-5xl text-[#0A0F1C] mb-4">{info?.name || 'Social Video Manager'}</h1>
            <p className="body-text text-[#475569] max-w-2xl mx-auto">
              {info?.tagline || 'Schedule and publish branded social posts and short videos from your admin.'}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Feature card */}
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 lg:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
                  <Video size={22} className="text-[#1D4ED8]" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Monthly</div>
                  <div className="text-3xl font-extrabold text-[#0A0F1C]">${info?.price_usd ?? 10}<span className="text-sm font-bold text-[#475569]">/mo</span></div>
                </div>
              </div>
              <p className="text-sm text-[#475569] mb-5">
                Add this on when you're ready to scale your visibility. Cancel anytime, no contracts.
              </p>
              <ul className="space-y-3" data-testid="social-video-features">
                {(info?.features || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#0A0F1C]">
                    <Check size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Subscribe form */}
            <form onSubmit={onSubscribe} className="bg-[#0F172A] text-white rounded-xl p-6 lg:p-8 shadow-md">
              <h2 className="text-xl font-bold mb-2">Start your subscription</h2>
              <p className="text-sm text-white/70 mb-5">You'll be redirected to a secure Stripe checkout to add your payment method.</p>
              <div className="space-y-3 mb-5">
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wider text-white/60 mb-1.5 inline-block">Full name</span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/15 rounded-md outline-none text-sm focus:border-[#F97316] focus:bg-white/15"
                    placeholder="First Last"
                    required
                    data-testid="social-video-name"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wider text-white/60 mb-1.5 inline-block">Email *</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/15 rounded-md outline-none text-sm focus:border-[#F97316] focus:bg-white/15"
                    placeholder="you@example.com"
                    required
                    data-testid="social-video-email"
                  />
                </label>
              </div>
              {error && (
                <div className="bg-red-500/20 border border-red-500/40 text-red-200 text-sm px-3 py-2 rounded-md mb-3" data-testid="social-video-error">{error}</div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#F97316] hover:bg-[#FB923C] text-white px-6 py-3 rounded-md font-bold text-sm transition-colors inline-flex items-center justify-center gap-2"
                data-testid="social-video-subscribe"
              >
                {loading ? <><Loader2 size={16} className="animate-spin" /> Starting…</> : <>Subscribe ${info?.price_usd ?? 10}/mo</>}
              </button>
              <p className="text-[11px] text-white/50 mt-3 text-center">Powered by Stripe · Cancel anytime from your portal</p>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
